import { supabase } from '../lib/supabase';

export interface SystemSetting {
  id: string;
  setting_key: string;
  setting_value: string | null;
  setting_type: 'string' | 'boolean' | 'number' | 'json';
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface CompanyBranding {
  company_name: string;
  company_logo_url: string;
}

// Cache for settings
let cachedSettings: Map<string, any> | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 300000; // 5 minute cache

export class SystemSettingsService {
  /**
   * Get a specific setting by key
   */
  static async getSetting(key: string): Promise<string | boolean | number | null> {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_value, setting_type')
        .eq('setting_key', key)
        .maybeSingle();

      if (error) {
        // Older DBs may lack setting_type column
        if ((error as { code?: string }).code === '42703') {
          const { data: row, error: err2 } = await supabase
            .from('system_settings')
            .select('setting_value')
            .eq('setting_key', key)
            .maybeSingle();
          if (err2 || row?.setting_value === null || row?.setting_value === undefined || row?.setting_value === '') {
            return null;
          }
          return this.coerceSettingValue(String(row.setting_value), null);
        }
        // PGRST116: no row — treat as missing key
        if ((error as { code?: string }).code === 'PGRST116') {
          return null;
        }
        console.error('Error fetching system setting:', error);
        return null;
      }

      if (!data) return null;

      const value = data.setting_value;
      if (value === null || value === undefined || value === '') return null;

      const st = (data as { setting_type?: string }).setting_type;
      return this.coerceSettingValue(String(value), st ?? null);
    } catch (error) {
      console.error('Error in getSetting:', error);
      return null;
    }
  }

  private static coerceSettingValue(
    value: string,
    settingType: string | null
  ): string | boolean | number | null {
    if (settingType === 'boolean') return value === 'true';
    if (settingType === 'number') return parseFloat(value);
    if (settingType === 'json') {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  }

  /**
   * Update a setting
   */
  static async updateSetting(
    key: string,
    value: string | boolean | number | object,
    description?: string
  ): Promise<void> {
    try {
      let settingType: 'string' | 'boolean' | 'number' | 'json' = 'string';
      let settingValue: string;

      if (typeof value === 'boolean') {
        settingType = 'boolean';
        settingValue = value.toString();
      } else if (typeof value === 'number') {
        settingType = 'number';
        settingValue = value.toString();
      } else if (typeof value === 'object') {
        settingType = 'json';
        settingValue = JSON.stringify(value);
      } else {
        settingValue = value;
      }

      const payload: Record<string, unknown> = {
        setting_key: key,
        setting_value: settingValue,
        setting_type: settingType,
        description: description || null,
        updated_at: new Date().toISOString(),
      };

      let { error } = await supabase.from('system_settings').upsert(payload, {
        onConflict: 'setting_key',
      });

      if (error && (error as { code?: string }).code === '42703') {
        delete payload.setting_type;
        const retry = await supabase.from('system_settings').upsert(payload, {
          onConflict: 'setting_key',
        });
        error = retry.error;
      }

      if (error) {
        console.error('Error updating system setting:', error);
        throw error;
      }

      // Clear cache
      this.clearCache();
    } catch (error) {
      console.error('Error in updateSetting:', error);
      throw error;
    }
  }

  /**
   * Get company branding (name and logo)
   */
  static async getCompanyBranding(): Promise<CompanyBranding> {
    try {
      const [name, logo] = await Promise.all([
        this.getSetting('company_name'),
        this.getSetting('company_logo_url')
      ]);

      return {
        company_name: (name as string) || 'Rapid Cert',
        company_logo_url: (logo as string) || ''
      };
    } catch (error) {
      console.error('Error getting company branding:', error);
      return {
        company_name: 'Rapid Cert',
        company_logo_url: ''
      };
    }
  }

  /**
   * Update company branding
   */
  static async updateCompanyBranding(
    name: string,
    logoUrl: string
  ): Promise<void> {
    try {
      await Promise.all([
        this.updateSetting('company_name', name, 'Company/Brand name displayed throughout the application'),
        this.updateSetting('company_logo_url', logoUrl, 'URL to company logo image')
      ]);
    } catch (error) {
      console.error('Error updating company branding:', error);
      throw error;
    }
  }

  /**
   * Upload logo to Supabase Storage
   */
  static async uploadLogo(file: File): Promise<string> {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `company-logo-${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('system-assets')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        const err = uploadError as { message?: string; statusCode?: string };
        const isBucketMissing =
          (err.message || '').toLowerCase().includes('bucket not found') ||
          err.statusCode === '404';

        if (isBucketMissing) {
          throw new Error(
            'Supabase Storage bucket "system-assets" does not exist in this project. Run the storage migration (20250120130003_setup_system_assets_storage.sql) or create bucket "system-assets" in Supabase Dashboard > Storage.'
          );
        }

        throw uploadError;
      }

      // Get public URL
      const { data } = supabase.storage
        .from('system-assets')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  }

  /**
   * Clear cache
   */
  static clearCache(): void {
    cachedSettings = null;
    cacheTimestamp = 0;
  }

  /**
   * Get all settings
   */
  static async getAllSettings(): Promise<SystemSetting[]> {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('*')
        .order('setting_key', { ascending: true });

      if (error) {
        console.error('Error fetching all settings:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllSettings:', error);
      throw error;
    }
  }
}

